import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  Buildings,
  MagnifyingGlass,
  Article,
  Sparkle,
  CircleNotch,
  CheckCircle,
  Trash,
  Copy,
  TrendUp,
  Target,
  ChartBar,
  Plus,
} from '@phosphor-icons/react';


export default function LeadGenSeoPage() {
  const [tab, setTab] = useState('discovery');

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="lead-gen-seo-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-fuchsia-500 to-rose-600 text-white flex items-center justify-center">
          <Sparkle size={22} weight="fill" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lead Gen + SEO</h1>
          <p className="text-sm text-slate-500">Bulk discovery, A/B testing y generador de articulos SEO</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-xl border border-slate-200 p-1">
        {[
          { v: 'discovery', l: 'Discovery Bulk', icon: Buildings },
          { v: 'ab', l: 'A/B Subjects', icon: ChartBar },
          { v: 'articles', l: 'Articulos SEO', icon: Article },
          { v: 'keywords', l: 'Keywords', icon: Target },
        ].map((t) => (
          <button
            key={t.v}
            onClick={() => setTab(t.v)}
            className={`flex items-center gap-2 flex-1 justify-center px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
              tab === t.v ? 'bg-gradient-to-r from-fuchsia-500 to-rose-600 text-white' : 'text-slate-600 hover:bg-slate-50'
            }`}
            data-testid={`tab-${t.v}-btn`}
          >
            <t.icon size={16} weight="fill" />
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'discovery' && <DiscoveryTab />}
      {tab === 'ab' && <ABSubjectsTab />}
      {tab === 'articles' && <ArticlesTab />}
      {tab === 'keywords' && <KeywordsTab />}
    </div>
  );
}


// ==================== DISCOVERY TAB ====================
function DiscoveryTab() {
  const [opts, setOpts] = useState({ industries: [], florida_cities: [] });
  const [selectedIndustries, setSelectedIndustries] = useState(['warehouses', 'hotels']);
  const [selectedCities, setSelectedCities] = useState(['Miami', 'Orlando']);
  const [target, setTarget] = useState(60);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/lead-gen/discovery-options`, { withCredentials: true })
      .then(r => setOpts(r.data))
      .catch(console.error);
  }, []);

  const toggle = (arr, item, setter) => {
    setter(arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item]);
  };

  const run = async () => {
    if (selectedIndustries.length === 0 || selectedCities.length === 0) {
      alert('Selecciona al menos 1 industria y 1 ciudad');
      return;
    }
    if (!window.confirm(`Iniciar descubrimiento bulk: ${selectedIndustries.length} industria(s) × ${selectedCities.length} ciudad(es) = ~${selectedIndustries.length * selectedCities.length} segmentos. Puede tardar 2-5 min.`)) return;
    setRunning(true);
    setResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/lead-gen/discover-bulk`, {
        industries: selectedIndustries,
        cities: selectedCities,
        min_employees: 20,
        target_count: target,
        auto_validate: true,
      }, { withCredentials: true, timeout: 600000 });
      setResult(res.data);
    } catch (e) {
      alert('Error: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5">
      <div>
        <h2 className="font-bold text-slate-900 mb-3">1. Selecciona industrias (SOP)</h2>
        <div className="flex flex-wrap gap-2">
          {opts.industries.map(ind => (
            <button
              key={ind.key}
              onClick={() => toggle(selectedIndustries, ind.key, setSelectedIndustries)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                selectedIndustries.includes(ind.key)
                  ? 'bg-fuchsia-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid={`ind-${ind.key}-btn`}
            >
              {ind.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-bold text-slate-900 mb-3">2. Ciudades de Florida</h2>
        <div className="flex flex-wrap gap-2">
          {opts.florida_cities.map(c => (
            <button
              key={c}
              onClick={() => toggle(selectedCities, c, setSelectedCities)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                selectedCities.includes(c)
                  ? 'bg-rose-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
              data-testid={`city-${c}-btn`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1">Target (total empresas a descubrir)</label>
        <input
          type="number"
          min={10}
          max={500}
          value={target}
          onChange={(e) => setTarget(parseInt(e.target.value) || 0)}
          className="w-32 px-3 py-2 border border-slate-200 rounded-lg text-sm"
          data-testid="target-count-input"
        />
        <p className="text-xs text-slate-400 mt-1">Se reparte entre todos los segmentos. Valida emails automaticamente.</p>
      </div>

      <button
        onClick={run}
        disabled={running}
        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-fuchsia-600 to-rose-600 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50"
        data-testid="run-discovery-btn"
      >
        {running ? <CircleNotch size={16} className="animate-spin" /> : <MagnifyingGlass size={16} weight="fill" />}
        {running ? 'Descubriendo... (2-5 min)' : 'Iniciar Bulk Discovery'}
      </button>

      {result && (
        <div className="border-t border-slate-100 pt-5" data-testid="discovery-result">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle size={24} className="text-emerald-600" weight="fill" />
            <div>
              <p className="font-bold text-slate-900">{result.total_saved} prospectos guardados</p>
              <p className="text-xs text-slate-500">{result.total_discovered} descubiertos · {result.total_discovered - result.total_saved} duplicados/inválidos</p>
            </div>
          </div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {result.segments.map((s, i) => (
              <div key={i} className="flex justify-between items-center text-sm bg-slate-50 px-3 py-2 rounded">
                <span className="font-medium">{s.industry} · {s.city}</span>
                <span className="text-emerald-700 font-bold">{s.saved} / {s.discovered}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== A/B SUBJECTS TAB ====================
function ABSubjectsTab() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingStage, setEditingStage] = useState(null);
  const [editVariants, setEditVariants] = useState([]);

  const refresh = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/lead-gen/subject-variants`, { withCredentials: true });
      setData(r.data || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const saveStage = async () => {
    try {
      await axios.post(`${API_URL}/api/lead-gen/subject-variants/${editingStage}`, { variants: editVariants.filter(v => v.trim()) }, { withCredentials: true });
      setEditingStage(null);
      refresh();
    } catch (e) {
      alert('Error: ' + e.message);
    }
  };

  if (loading) return <div className="text-center py-10"><CircleNotch size={32} className="animate-spin text-fuchsia-600" /></div>;

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">El sistema rota variantes automaticamente. Tras 30 envios por variante, escoge la de mejor open/reply rate.</p>
      {Object.entries(data).map(([stage, variants]) => (
        <div key={stage} className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-bold capitalize">{stage.replace(/_/g, ' ')}</h3>
            <button
              onClick={() => { setEditingStage(stage); setEditVariants(variants.map(v => v.template)); }}
              className="text-xs px-3 py-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700 font-semibold"
              data-testid={`edit-${stage}-btn`}
            >
              Editar Variantes
            </button>
          </div>
          {editingStage === stage ? (
            <div className="space-y-2">
              {editVariants.map((v, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    value={v}
                    onChange={(e) => { const cp = [...editVariants]; cp[i] = e.target.value; setEditVariants(cp); }}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded text-sm"
                    placeholder="Subject (use {company} y {industry} como placeholders)"
                  />
                  <button onClick={() => setEditVariants(editVariants.filter((_, idx) => idx !== i))} className="px-2 text-red-500"><Trash size={14} /></button>
                </div>
              ))}
              <button onClick={() => setEditVariants([...editVariants, ''])} className="text-xs text-fuchsia-600 font-semibold flex items-center gap-1"><Plus size={12} weight="bold" /> Agregar variante</button>
              <div className="flex gap-2 pt-2">
                <button onClick={saveStage} className="px-4 py-1.5 bg-fuchsia-600 text-white rounded text-sm font-semibold">Guardar</button>
                <button onClick={() => setEditingStage(null)} className="px-4 py-1.5 text-slate-600 text-sm">Cancelar</button>
              </div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-slate-500">
                <tr><th className="text-left">Variante</th><th className="text-center w-16">Sent</th><th className="text-center w-20">Open %</th><th className="text-center w-20">Reply %</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {variants.map(v => (
                  <tr key={v.id}>
                    <td className="py-2 text-slate-700">{v.template}</td>
                    <td className="text-center font-medium">{v.sent}</td>
                    <td className="text-center"><span className={`font-bold ${v.open_rate >= 40 ? 'text-emerald-600' : 'text-slate-400'}`}>{v.open_rate}%</span></td>
                    <td className="text-center"><span className={`font-bold ${v.reply_rate >= 3 ? 'text-emerald-600' : 'text-slate-400'}`}>{v.reply_rate}%</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}


// ==================== SEO ARTICLES TAB ====================
function ArticlesTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    topic: 'Cost of fiber optic installation in Florida warehouses',
    target_keyword: 'fiber optic installation Florida',
    secondary_keywords_text: 'commercial fiber installation, low voltage Florida, fiber for warehouses',
    target_word_count: 1200,
    industry_focus: 'warehouse',
  });
  const [generating, setGenerating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const refresh = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/lead-gen/seo/articles`, { withCredentials: true });
      setArticles(r.data?.articles || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const body = {
        topic: form.topic,
        target_keyword: form.target_keyword,
        secondary_keywords: form.secondary_keywords_text.split(',').map(s => s.trim()).filter(Boolean),
        target_word_count: form.target_word_count,
        tone: 'professional',
        industry_focus: form.industry_focus,
      };
      const res = await axios.post(`${API_URL}/api/lead-gen/seo/generate-article`, body, { withCredentials: true, timeout: 300000 });
      alert(`Articulo generado: ${res.data.word_count_actual} palabras`);
      refresh();
    } catch (e) {
      alert('Error: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const viewArticle = async (id) => {
    try {
      const r = await axios.get(`${API_URL}/api/lead-gen/seo/article/${id}`, { withCredentials: true });
      setViewing(r.data);
    } catch (e) {
      alert(e.message);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    await axios.delete(`${API_URL}/api/lead-gen/seo/article/${id}`, { withCredentials: true });
    refresh();
    if (viewing?.id === id) setViewing(null);
  };

  const copyMarkdown = () => {
    if (!viewing?.markdown) return;
    navigator.clipboard.writeText(viewing.markdown);
    alert('Markdown copiado');
  };

  return (
    <div className="space-y-4">
      {/* Generator form */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="font-bold text-slate-900 flex items-center gap-2"><Sparkle size={16} weight="fill" /> Generar artículo SEO</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Tópico</label>
            <input value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded text-sm" data-testid="topic-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Keyword principal</label>
            <input value={form.target_keyword} onChange={e => setForm({ ...form, target_keyword: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded text-sm" data-testid="keyword-input" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-slate-600">Keywords secundarias (separadas por coma)</label>
            <input value={form.secondary_keywords_text} onChange={e => setForm({ ...form, secondary_keywords_text: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Palabras objetivo</label>
            <input type="number" min={500} max={3000} value={form.target_word_count} onChange={e => setForm({ ...form, target_word_count: parseInt(e.target.value) || 1200 })} className="w-full px-3 py-2 border border-slate-200 rounded text-sm" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Industria foco</label>
            <select value={form.industry_focus} onChange={e => setForm({ ...form, industry_focus: e.target.value })} className="w-full px-3 py-2 border border-slate-200 rounded text-sm">
              <option value="">Sin industria específica</option>
              <option value="warehouse">Warehouse</option>
              <option value="hotel">Hotel</option>
              <option value="commercial">Commercial Building</option>
              <option value="healthcare">Healthcare</option>
            </select>
          </div>
        </div>
        <button onClick={generate} disabled={generating} className="px-5 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-lg font-semibold disabled:opacity-50 flex items-center gap-2" data-testid="generate-article-btn">
          {generating ? <CircleNotch size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />}
          {generating ? 'Generando... (30-60s)' : 'Generar Artículo'}
        </button>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold mb-3">Artículos guardados ({articles.length})</h3>
        {loading ? <CircleNotch size={24} className="animate-spin" /> : articles.length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center">Aún no hay artículos generados</p>
        ) : (
          <div className="space-y-2">
            {articles.map(a => (
              <div key={a.id} className="flex items-center justify-between p-3 bg-slate-50 rounded hover:bg-slate-100 cursor-pointer" onClick={() => viewArticle(a.id)} data-testid={`article-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{a.h1 || a.meta_title}</p>
                  <p className="text-xs text-slate-500">{a.target_keyword || a.request?.target_keyword} · {a.word_count_actual || a.word_count} palabras · {a.created_at?.split('T')[0]}</p>
                </div>
                <button onClick={(e) => { e.stopPropagation(); deleteArticle(a.id); }} className="text-red-400 hover:text-red-600 p-2" data-testid={`delete-article-${a.id}`}><Trash size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Viewer modal */}
      {viewing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setViewing(null)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <p className="font-bold text-slate-900">{viewing.h1}</p>
                <p className="text-xs text-slate-500">{viewing.meta_description}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={copyMarkdown} className="px-3 py-1.5 bg-slate-100 rounded text-xs font-semibold flex items-center gap-1"><Copy size={12} /> Copiar Markdown</button>
                <button onClick={() => setViewing(null)} className="px-3 py-1.5 text-slate-600">Cerrar</button>
              </div>
            </div>
            <div className="overflow-y-auto p-6 prose prose-sm max-w-none">
              <pre className="whitespace-pre-wrap font-mono text-xs bg-slate-50 p-4 rounded">{viewing.markdown}</pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== KEYWORDS TAB ====================
function KeywordsTab() {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ keyword: '', priority: 5, target_position: 10 });

  const refresh = async () => {
    try {
      setLoading(true);
      const r = await axios.get(`${API_URL}/api/lead-gen/seo/keywords`, { withCredentials: true });
      setKeywords(r.data?.keywords || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!form.keyword.trim()) return;
    try {
      await axios.post(`${API_URL}/api/lead-gen/seo/keywords`, form, { withCredentials: true });
      setForm({ keyword: '', priority: 5, target_position: 10 });
      refresh();
    } catch (e) {
      alert(e.message);
    }
  };

  const updatePos = async (id, pos) => {
    if (!pos || isNaN(pos)) return;
    await axios.put(`${API_URL}/api/lead-gen/seo/keywords/${id}/position`, { position: pos }, { withCredentials: true });
    refresh();
  };

  const del = async (id) => {
    if (!window.confirm('¿Eliminar?')) return;
    await axios.delete(`${API_URL}/api/lead-gen/seo/keywords/${id}`, { withCredentials: true });
    refresh();
  };

  if (loading) return <div className="text-center py-10"><CircleNotch size={32} className="animate-spin text-fuchsia-600" /></div>;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
        <h3 className="font-bold text-slate-900">Agregar keyword a trackear</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <input placeholder="Keyword" value={form.keyword} onChange={e => setForm({ ...form, keyword: e.target.value })} className="md:col-span-2 px-3 py-2 border border-slate-200 rounded text-sm" data-testid="kw-input" />
          <input type="number" min={1} max={10} placeholder="Prioridad 1-10" value={form.priority} onChange={e => setForm({ ...form, priority: parseInt(e.target.value) })} className="px-3 py-2 border border-slate-200 rounded text-sm" />
          <input type="number" min={1} max={100} placeholder="Posicion meta" value={form.target_position} onChange={e => setForm({ ...form, target_position: parseInt(e.target.value) })} className="px-3 py-2 border border-slate-200 rounded text-sm" />
        </div>
        <button onClick={add} className="px-4 py-2 bg-fuchsia-600 text-white rounded text-sm font-semibold flex items-center gap-1" data-testid="add-kw-btn"><Plus size={14} weight="bold" /> Agregar</button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-bold mb-3">Keywords trackeadas ({keywords.length})</h3>
        {keywords.length === 0 ? (
          <p className="text-slate-400 text-sm py-6 text-center">Sin keywords. Agrega tus 5-10 keywords principales para empezar.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase">
              <tr><th className="text-left">Keyword</th><th className="text-center w-20">Prio</th><th className="text-center w-24">Posicion</th><th className="text-center w-24">Meta</th><th className="text-center w-32">Actualizar</th><th className="w-12"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {keywords.map(k => {
                const meetsTarget = k.current_position && k.current_position <= k.target_position;
                return (
                  <tr key={k.id} data-testid={`kw-${k.id}`}>
                    <td className="py-2 font-medium">{k.keyword}</td>
                    <td className="text-center">{k.priority}</td>
                    <td className="text-center"><span className={`font-bold ${meetsTarget ? 'text-emerald-600' : k.current_position ? 'text-amber-600' : 'text-slate-300'}`}>{k.current_position || '—'}</span></td>
                    <td className="text-center text-slate-500">≤ {k.target_position}</td>
                    <td className="text-center">
                      <input type="number" min={1} max={100} placeholder="Pos" onBlur={(e) => { if (e.target.value) { updatePos(k.id, parseInt(e.target.value)); e.target.value = ''; }}} className="w-16 px-2 py-1 border border-slate-200 rounded text-xs text-center" data-testid={`update-pos-${k.id}`} />
                    </td>
                    <td className="text-center"><button onClick={() => del(k.id)} className="text-red-400 hover:text-red-600"><Trash size={14} /></button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
        <p className="font-bold mb-1">💡 Cómo trackear posiciones</p>
        <p>Por ahora el tracking es manual: busca tu keyword en Google (en incógnito) y registra la posición en la columna "Actualizar". Para automatizarlo, necesitas conectar Google Search Console (~3h adicionales).</p>
      </div>
    </div>
  );
}
